import React from 'react';
import { cn } from '@/utils/cn';

export type CheckboxSize = 's' | 'm' | 'l';
export type CheckboxState =
  | 'default'
  | 'checked'
  | 'checked-minus'
  | 'disabled'
  | 'checked-disabled'
  | 'checked-minus-disabled';

export interface TeeCheckboxProps {
  /**
   * Checkbox size
   * @default "m"
   */
  size?: CheckboxSize;

  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;

  /**
   * Whether the checkbox is indeterminate (minus state)
   */
  indeterminate?: boolean;

  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;

  /**
   * Callback when checkbox state changes
   */
  onChange?: (checked: boolean) => void;

  /**
   * Label text (optional)
   */
  label?: string;

  /**
   * Checkbox name attribute
   */
  name?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Additional container classes
   */
  containerClassName?: string;
}

export const TeeCheckbox = React.forwardRef<HTMLInputElement, TeeCheckboxProps>(
  (
    {
      size = 'm',
      checked = false,
      indeterminate = false,
      disabled = false,
      onChange,
      label,
      name,
      className,
      containerClassName,
    },
    ref
  ) => {
    const sizeClasses = {
      s: 'size-[16px] rounded-[3px]',
      m: 'size-[20px] rounded-[var(--spacing-border-radius-2xs,4px)]',
      l: 'size-[26px] rounded-[6px]',
    };

    const innerBoxSizeClasses = {
      s: 'size-[12px] rounded-[2px]',
      m: 'size-[16px] rounded-[2px]',
      l: 'size-[21px] rounded-[4px]',
    };

    const borderClasses = disabled
      ? 'border-[var(--border-radio-checkbox-default,#999)]'
      : checked || indeterminate
        ? 'border-[var(--border-radio-checkbox-filled-dark,#16241E)]'
        : 'border-[var(--border-radio-checkbox-default,#999)]';

    const bgClasses =
      disabled && !checked && !indeterminate
        ? 'bg-[var(--background-radio-checkbox-disabled,#f1f1f1)]'
        : 'bg-[var(--background-radio-checkbox-default,white)]';

    const innerBgClasses = disabled
      ? 'bg-[var(--background-radio-checkbox-disabled,#f1f1f1)]'
      : checked || indeterminate
        ? 'bg-[var(--background-radio-checkbox-filled-dark,#16241E)]'
        : '';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled && onChange) {
        onChange(e.target.checked);
      }
    };

    return (
      <div className={cn('flex items-center gap-2', containerClassName)}>
        <div className="relative">
          <input
            ref={ref}
            type="checkbox"
            name={name}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />

          <div
            className={cn(
              'border border-solid flex items-center justify-center',
              sizeClasses[size],
              bgClasses,
              borderClasses,
              !disabled && 'cursor-pointer',
              className
            )}
          >
            {(checked || indeterminate) && (
              <div
                className={cn(
                  innerBoxSizeClasses[size],
                  innerBgClasses,
                  'flex items-center justify-center'
                )}
              >
                {indeterminate ? (
                  <svg
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            )}
          </div>
        </div>

        {label && (
          <label
            className={cn(
              'text-body-s font-normal',
              disabled
                ? 'text-[var(--text-label-disabled)]'
                : 'text-[var(--text-label-sub-label)]',
              !disabled && 'cursor-pointer'
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

TeeCheckbox.displayName = 'TeeCheckbox';

export { TeeCheckbox as default };
